import type { PublicPageId, PublicRouteKind, PublicRouteOwner } from './types';

export type StaticDocumentRouteId =
  | 'home'
  | 'about'
  | 'accessibility'
  | 'contact'
  | 'cookies'
  | 'copyright'
  | 'ethics'
  | 'help'
  | 'notice'
  | 'privacy'
  | 'terms';

export type AppShellRouteId =
  | 'space-write'
  | 'space-read'
  | 'feedback'
  | 'presence'
  | 'account-claim'
  | 'account-home'
  | 'account-login'
  | 'account-register'
  | 'account-recover'
  | 'account-texts'
  | 'account-saved'
  | 'account-recent'
  | 'account-reading'
  | 'account-translations'
  | 'account-bonus'
  | 'account-support'
  | 'account-security'
  | 'account-settings';

export type PublicRouteSpec = {
  routeRole?: 'text-index' | 'text-detail';
  id: PublicPageId;
  kind: PublicRouteKind;
  owner: PublicRouteOwner;
  slug: string;
  path: string;
  contentKey?: string;
  pageType?: string;
  runtime: boolean;
  jsRequiredForPrimaryContent: boolean;
  cachePolicy: {
    cacheControl: string;
    mayCacheAtCdn: boolean;
    private: boolean;
  };
  directlyOpenable: true;
  clientRouterMayEnhance: boolean;
  clientRouterMayBeSourceOfTruth: false;
};

export type DynamicPublicRouteSpec = PublicRouteSpec & {
  pattern: string;
  allowHashManage?: boolean;
  rendererContract?: {
    module: string;
    apiModel: string;
    primaryContentReadableWithoutJavaScript: boolean;
    apiOwnsHtml: false;
    publicLayerOwnsHtml: true;
    hydrationContainsBody: boolean;
    manageMode: '/t/:publicId#manage=TOKEN';
    canonicalMode: '/t' | '/t/:publicId' | 'none-controlled-entry';
  };
};

export const STATIC_DOCUMENT_ROUTES: PublicRouteSpec[] = [
  staticDocument('home', '', '/', 'home', 'static'),
  staticDocument('about', 'about', '/about', 'about', 'static'),
  staticDocument('accessibility', 'accessibility', '/accessibility', 'accessibility', 'legal'),
  staticDocument('contact', 'contact', '/contact', 'contact', 'static'),
  staticDocument('cookies', 'cookies', '/cookies', 'cookies', 'legal'),
  staticDocument('copyright', 'copyright', '/copyright', 'copyright', 'legal'),
  staticDocument('ethics', 'ethics', '/ethics', 'ethics', 'legal'),
  staticDocument('help', 'help', '/help', 'help', 'static'),
  staticDocument('notice', 'notice', '/notice', 'notice', 'legal'),
  staticDocument('privacy', 'privacy', '/privacy', 'privacy', 'legal'),
  staticDocument('terms', 'terms', '/terms', 'terms', 'legal'),
];

export const APP_SHELL_ROUTES: PublicRouteSpec[] = [
  appShell('space-write', 'write', '/write', 'write', 'space-write'),
  appShell('space-read', 'read', '/read', 'read', 'space-reading'),
  appShell('feedback', 'feedback', '/feedback', 'feedback', 'static'),
  appShell('presence', 'presence', '/presence', 'presence', 'presence'),
  appShell('account-claim', 'account/claim', '/account/claim', 'account/claim', 'space-claim'),
  appShell('account-home', 'account', '/account', 'account', 'account-home'),
  appShell('account-login', 'account/login', '/account/login', 'account/login', 'account-login'),
  appShell(
    'account-register',
    'account/register',
    '/account/register',
    'account/register',
    'account-register',
  ),
  appShell(
    'account-recover',
    'account/recover',
    '/account/recover',
    'account/recover',
    'account-recover',
  ),
  appShell('account-texts', 'account/texts', '/account/texts', 'account/texts', 'account-texts'),
  appShell('account-saved', 'account/saved', '/account/saved', 'account/saved', 'account-saved'),
  appShell(
    'account-recent',
    'account/recent',
    '/account/recent',
    'account/recent',
    'account-recent',
  ),
  appShell(
    'account-reading',
    'account/reading',
    '/account/reading',
    'account/reading',
    'account-reading',
  ),
  appShell(
    'account-translations',
    'account/translations',
    '/account/translations',
    'account/translations',
    'account-translations',
  ),
  appShell('account-bonus', 'account/bonus', '/account/bonus', 'account/bonus', 'account-bonus'),
  appShell(
    'account-support',
    'account/support',
    '/account/support',
    'account/support',
    'account-support',
  ),
  appShell(
    'account-security',
    'account/security',
    '/account/security',
    'account/security',
    'account-security',
  ),
  appShell(
    'account-settings',
    'account/settings',
    '/account/settings',
    'account/settings',
    'account-settings',
  ),
];

export const DYNAMIC_TEXT_ROUTES: DynamicPublicRouteSpec[] = [
  {
    id: 'space-text',
    kind: 'dynamic-text',
    owner: 'public-edge-renderer',
    routeRole: 'text-index',
    slug: 't',
    path: '/t',
    pattern: '/t',
    contentKey: 't',
    pageType: 'space-text',
    runtime: true,
    jsRequiredForPrimaryContent: false,
    cachePolicy: {
      cacheControl: 'public, max-age=60, stale-while-revalidate=300',
      mayCacheAtCdn: true,
      private: false,
    },
    directlyOpenable: true,
    clientRouterMayEnhance: true,
    clientRouterMayBeSourceOfTruth: false,
    allowHashManage: false,
    rendererContract: {
      module: 'src/public-renderer/renderSpaceTextIndexDocument.ts',
      apiModel: 'PublicTextMissingIdRenderModel',
      primaryContentReadableWithoutJavaScript: true,
      apiOwnsHtml: false,
      publicLayerOwnsHtml: true,
      hydrationContainsBody: false,
      manageMode: '/t/:publicId#manage=TOKEN',
      canonicalMode: '/t',
    },
  },
  {
    id: 'space-text',
    kind: 'dynamic-text',
    owner: 'public-edge-renderer',
    routeRole: 'text-detail',
    slug: 't/:publicId',
    path: '/t/:publicId',
    pattern: '/t/:publicId',
    contentKey: 't',
    pageType: 'space-text',
    runtime: true,
    jsRequiredForPrimaryContent: true,
    cachePolicy: {
      cacheControl: 'public, max-age=60, stale-while-revalidate=300',
      mayCacheAtCdn: true,
      private: false,
    },
    directlyOpenable: true,
    clientRouterMayEnhance: true,
    clientRouterMayBeSourceOfTruth: false,
    allowHashManage: true,
    rendererContract: {
      module: 'src/public-renderer/renderSpaceTextDocument.ts',
      apiModel: 'PublicTextRenderModel',
      primaryContentReadableWithoutJavaScript: false,
      apiOwnsHtml: false,
      publicLayerOwnsHtml: true,
      hydrationContainsBody: false,
      manageMode: '/t/:publicId#manage=TOKEN',
      canonicalMode: 'none-controlled-entry',
    },
  },
];

export const COMPATIBILITY_SHELL_ROUTES: PublicRouteSpec[] = [];

export const BLOCKED_PUBLIC_ROUTES = ['/manage/:publicId'];

export const PUBLIC_ROUTE_CATALOG = {
  staticDocuments: STATIC_DOCUMENT_ROUTES,
  appShells: APP_SHELL_ROUTES,
  dynamicText: DYNAMIC_TEXT_ROUTES,
  compatibilityShells: COMPATIBILITY_SHELL_ROUTES,
  blocked: BLOCKED_PUBLIC_ROUTES,
} as const;

export const STATIC_DOCUMENT_SLUGS = new Set(STATIC_DOCUMENT_ROUTES.map((route) => route.slug));

export const APP_SHELL_SLUGS = new Set(APP_SHELL_ROUTES.map((route) => route.slug));

export const COMPATIBILITY_SHELL_SLUGS = new Set(
  COMPATIBILITY_SHELL_ROUTES.map((route) => route.slug),
);

function staticDocument(
  id: StaticDocumentRouteId,
  slug: string,
  path: string,
  contentKey: string,
  pageType: string,
): PublicRouteSpec {
  return {
    id: id === 'home' ? 'home' : 'static-document',
    kind: 'static',
    owner: 'eleventy-static',
    slug,
    path,
    contentKey,
    pageType,
    runtime: false,
    jsRequiredForPrimaryContent: false,
    cachePolicy: {
      cacheControl: 'public, max-age=300, stale-while-revalidate=86400',
      mayCacheAtCdn: true,
      private: false,
    },
    directlyOpenable: true,
    clientRouterMayEnhance: false,
    clientRouterMayBeSourceOfTruth: false,
  };
}

function appShell(
  id: AppShellRouteId,
  slug: string,
  path: string,
  contentKey: string,
  pageType: string,
): PublicRouteSpec {
  return {
    id,
    kind: 'app-shell',
    owner: 'eleventy-app-shell',
    slug,
    path,
    contentKey,
    pageType,
    runtime: true,
    jsRequiredForPrimaryContent: true,
    cachePolicy: id.startsWith('account-')
      ? { cacheControl: 'no-store', mayCacheAtCdn: false, private: true }
      : {
          cacheControl: 'public, max-age=60, stale-while-revalidate=300',
          mayCacheAtCdn: true,
          private: false,
        },
    directlyOpenable: true,
    clientRouterMayEnhance: !id.startsWith('account-'),
    clientRouterMayBeSourceOfTruth: false,
  };
}
